import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface Booking {
  reference: string;
  status: string;
  checkin_date: string;
  checkout_date: string;
  total_cents: number;
  full_name: string;
  phone: string;
}

export default function AdminDashboard() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("PENDING_PAYMENT");

  const bookings = useQuery({
    queryKey: ["admin-bookings", statusFilter],
    queryFn: async () => {
      const res = await fetch(`/api/v1/admin/bookings?status=${statusFilter}`, {
        credentials: "include",
      });
      if (res.status === 401) throw new Error("UNAUTHENTICATED");
      if (!res.ok) throw new Error("Ralat memuatkan tempahan");
      const data = (await res.json()) as { bookings: Booking[] }; return data.bookings;
      return data.bookings as Booking[];
    },
    retry: false,
  });

  const confirm = useMutation({
    mutationFn: async (reference: string) => {
      const res = await fetch(`/api/v1/admin/bookings/${reference}/confirm`, {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { error?: string; ok?: boolean; status?: string };
      if (!res.ok) throw new Error(data.error ?? "Ralat mengesahkan bayaran");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-bookings"] }),
  });

  const reject = useMutation({
    mutationFn: async (reference: string) => {
      const res = await fetch(`/api/v1/admin/bookings/${reference}/reject`, {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { error?: string; ok?: boolean };
      if (!res.ok) throw new Error(data.error ?? "Ralat menolak bayaran");
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-bookings"] }),
  });

  if (bookings.isError && (bookings.error as Error).message === "UNAUTHENTICATED") {
    window.location.href = "/admin/login";
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <h1 className="text-2xl font-bold text-sea-700 mb-6">Dashboard Admin — Tempahan</h1>

      <div className="flex gap-2 mb-4">
        {["PENDING_PAYMENT", "PAYMENT_REVIEW", "CONFIRMED", "CANCELLED"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-sm ${
              statusFilter === s ? "bg-sea-500 text-white" : "bg-white text-sea-700"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm divide-y">
        {bookings.data?.length === 0 && (
          <p className="p-6 text-sea-500 text-sm">Tiada tempahan dengan status ini.</p>
        )}
        {bookings.data?.map((b) => (
          <div key={b.reference} className="p-4 flex items-center justify-between text-sm">
            <div>
              <p className="font-mono font-bold">{b.reference}</p>
              <p>{b.full_name} &middot; {b.phone}</p>
              <p className="text-sea-500">
                {b.checkin_date} — {b.checkout_date} &middot; RM{(b.total_cents / 100).toFixed(2)}
              </p>
            </div>
            {(b.status === "PENDING_PAYMENT" || b.status === "PAYMENT_REVIEW") && (
              <div className="flex gap-2">
                <button
                  onClick={() => confirm.mutate(b.reference)}
                  disabled={confirm.isPending}
                  className="bg-green-500 text-white rounded-lg px-3 py-1.5 hover:bg-green-600 transition"
                >
                  Sahkan
                </button>
                <button
                  onClick={() => reject.mutate(b.reference)}
                  disabled={reject.isPending}
                  className="bg-red-500 text-white rounded-lg px-3 py-1.5 hover:bg-red-600 transition"
                >
                  Tolak
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* TODO Fasa 6 lanjutan: kalendar, pengurusan harga, kandungan, pengguna, laporan CSV */}
    </div>
  );
}
